import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Checkbox from "@material-ui/core/Checkbox";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { formatMontevideoDate, formatMontevideoTime, isSameMontevideoDay } from "../../utils/dateTime";
import ChannelBadge from "../ChannelBadge";

const READ_TICKET_SNAPSHOTS_KEY = "crm-read-ticket-snapshots";

const ticketReadMarker = ticket =>
	`${ticket?.updatedAt || ""}|${ticket?.lastMessage || ""}`;

const getReadSnapshot = ticketId => {
	try {
		const snapshots = JSON.parse(localStorage.getItem(READ_TICKET_SNAPSHOTS_KEY) || "{}");
		return snapshots[String(ticketId)] || "";
	} catch (error) {
		return "";
	}
};

const saveReadSnapshot = ticket => {
	try {
		const snapshots = JSON.parse(localStorage.getItem(READ_TICKET_SNAPSHOTS_KEY) || "{}");
		snapshots[String(ticket.id)] = ticketReadMarker(ticket);
		localStorage.setItem(READ_TICKET_SNAPSHOTS_KEY, JSON.stringify(snapshots));
	} catch (error) {
		// localStorage may be unavailable in private browsing; the UI still works.
	}
};

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
		minHeight: 72,
		padding: theme.spacing(0.75, 0.9, 0.7, 1.1),
		borderRadius: 10,
		margin: theme.spacing(0.35, 0.6),
		width: "auto",
		transition: "background-color 150ms ease, border-color 150ms ease, transform 150ms ease",
		border: "1px solid transparent",
		"&:hover": {
			backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,.055)" : "#f8f7fb",
			transform: "translateY(-1px)",
		},
		"&.Mui-selected": {
			backgroundColor: `${theme.palette.primary.main}10`,
			borderColor: `${theme.palette.primary.main}45`,
			boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
			"&:hover": { backgroundColor: `${theme.palette.primary.main}16` },
		},
		"& .MuiListItemAvatar-root": { minWidth: 42 },
		"& .MuiAvatar-root": { width: 34, height: 34, border: `1px solid ${theme.palette.background.paper}` },
	},

	unreadTicket: {
		backgroundColor: "#eaf5ff",
		borderColor: "#c8e5fb",
		boxShadow: "inset 4px 0 0 #69b7f2",
		color: "#102a43",
		"& .MuiTypography-colorTextPrimary, & .MuiTypography-root, & p, & span": {
			color: "#102a43 !important",
		},
		"& .MuiTypography-colorTextSecondary": {
			color: "#486581 !important",
		},
		"& .MuiSvgIcon-root": {
			color: "#486581",
		},
		"&:hover": {
			backgroundColor: "#d7ecfc",
		},
	},

	pendingTicket: {
		cursor: "unset",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: theme.spacing(1),
		minWidth: 0,
	},
	contactName: {
		fontWeight: 780,
		fontSize: ".84rem",
		letterSpacing: "-.01em",
	},

	lastMessageTime: {
		justifySelf: "flex-end",
		flex: "none",
		whiteSpace: "nowrap",
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		minWidth: 0,
		flex: 1,
		fontSize: ".74rem",
		lineHeight: 1.2,
	},
	messageAuthor: {
		fontWeight: 800,
		color: theme.palette.text.primary,
		marginRight: 4,
	},

	secondaryWrapper: {
		display: "block",
		minWidth: 0,
	},

	messageRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		minWidth: 0,
	},

	channelRow: {
		display: "flex",
		alignItems: "center",
		gap: 5,
		minWidth: 0,
		marginTop: 2,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginRight: 8,
		marginLeft: "auto",
	},

	badgeStyle: {
		color: "white",
		backgroundColor: "#ef4f5a",
		fontWeight: 800,
		minWidth: 18,
		height: 18,
		borderRadius: 10,
		boxShadow: "0 3px 10px rgba(239,79,90,.25)",
	},

	pendingReplyBadge: {
		display: "inline-flex",
		alignItems: "center",
		gap: 5,
		marginLeft: "auto",
		padding: "3px 7px 3px 4px",
		borderRadius: 999,
		color: "#8a4b00",
		backgroundColor: "#fff2cf",
		border: "1px solid #f5c45d",
		fontSize: ".64rem",
		fontWeight: 800,
		whiteSpace: "nowrap",
	},
	pendingReplyCount: {
		display: "inline-grid",
		placeItems: "center",
		minWidth: 18,
		height: 18,
		padding: "0 4px",
		borderRadius: 9,
		color: "#fff",
		backgroundColor: "#f59e0b",
		boxShadow: "0 3px 9px rgba(245,158,11,.24)",
	},
	answeredBadge: {
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		marginLeft: "auto",
		padding: "3px 7px",
		borderRadius: 999,
		color: "#17603c",
		backgroundColor: "#e3f5e9",
		border: "1px solid #a8dfba",
		fontSize: ".64rem",
		fontWeight: 800,
		whiteSpace: "nowrap",
		"& svg": { fontSize: 15, color: "#22844f !important" },
	},

	acceptButton: {
		position: "absolute",
		left: "50%",
	},

	ticketQueueColor: {
		flex: "none",
		width: "3px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
		borderRadius: "0 4px 4px 0",
	},

}));

const TicketListItem = ({ ticket, selectionMode = false, selectedForBulk = false, onToggleSelect }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isSelected = ticketId && +ticketId === ticket.id;
	const currentReadMarker = ticketReadMarker(ticket);
	const [readMarker, setReadMarker] = useState(() => getReadSnapshot(ticket.id));
	const isLocallyRead = readMarker === currentReadMarker && currentReadMarker !== "";
	const isUnread = Number(ticket.unreadMessages || 0) > 0 && !isLocallyRead && !isSelected;
	const needsReply = Boolean(ticket.lastMessage && (ticket.needsReply === true || ticket.lastMessageFromMe === false));
	const pendingCustomerMessages = needsReply
		? Math.max(1, Number(ticket.pendingCustomerMessages || ticket.unreadMessages || 0))
		: 0;
	const answered = Boolean(ticket.lastMessage && ticket.lastMessageFromMe === true);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		saveReadSnapshot(ticket);
		setReadMarker(currentReadMarker);
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		saveReadSnapshot(ticket);
		setReadMarker(currentReadMarker);
		history.push(`/tickets/${id}`);
	};

	const prefetchMessages = id => {
		api.get(`/tickets/${id}`).catch(() => undefined);
		api.get(`/messages/${id}`).catch(() => undefined);
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onMouseEnter={() => prefetchMessages(ticket.id)}
				onFocus={() => prefetchMessages(ticket.id)}
				onTouchStart={() => prefetchMessages(ticket.id)}
				 onClick={e => {
					if (selectionMode) {
						e.stopPropagation();
						if (ticket.isDemo) return;
						onToggleSelect?.(ticket.id);
						return;
					}
					if (ticket.status === "pending") return;
					handleSelectTicket(ticket.id);
				}}
				selected={isSelected}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
					[classes.unreadTicket]: isUnread,
				})}
			>
				{selectionMode && !ticket.isDemo && <Checkbox size="small" color="primary" checked={selectedForBulk} onChange={() => onToggleSelect?.(ticket.id)} onClick={e => e.stopPropagation()} inputProps={{ "aria-label": `Seleccionar chat de ${ticket.contact?.name || "cliente"}` }} />}
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Sin cola"}
				>
					<span
						style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>
				<ListItemAvatar>
					<Avatar src={ticket?.contact?.profilePicUrl} />
				</ListItemAvatar>
				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
								className={classes.contactName}
							>
								{ticket.contact.name}
							</Typography>
							{ticket.status === "closed" && (
								<Badge
									className={classes.closedBadge}
									badgeContent={"closed"}
									color="primary"
								/>
							)}
							{ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{isSameMontevideoDay(ticket.updatedAt, new Date()) ? (
										<>{formatMontevideoTime(ticket.updatedAt)}</>
									) : (
										<>{formatMontevideoDate(ticket.updatedAt)}</>
									)}
								</Typography>
							)}
						</span>
					}
					secondary={
							<span className={classes.secondaryWrapper}>
								<span className={classes.messageRow}>
									<Typography
										className={classes.contactLastMessage}
										noWrap
										component="span"
										variant="body2"
										color="textSecondary"
									>
										{ticket.lastMessage ? (
											<>
												<span className={classes.messageAuthor}>{ticket.lastMessageFromMe ? "Vos:" : "Cliente:"}</span>
												<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
											</>
										) : (
											<br />
										)}
									</Typography>

								</span>
								<span className={classes.channelRow}>
									<ChannelBadge ticket={ticket} />
									{needsReply ? (
										<Tooltip arrow title={`${pendingCustomerMessages} mensaje${pendingCustomerMessages === 1 ? "" : "s"} del cliente espera${pendingCustomerMessages === 1 ? "" : "n"} respuesta`}>
											<span className={classes.pendingReplyBadge} aria-label={`${pendingCustomerMessages} mensajes pendientes de respuesta`}>
												<span className={classes.pendingReplyCount}>{pendingCustomerMessages}</span>
												Sin responder
											</span>
										</Tooltip>
									) : answered ? (
										<Tooltip arrow title="El último mensaje fue enviado por tu equipo">
											<span className={classes.answeredBadge} aria-label="Conversación respondida"><CheckCircleOutlineIcon /> Respondida</span>
										</Tooltip>
									) : null}
								</span>
							</span>
					}
				/>
				{ticket.status === "pending" && (
					<ButtonWithSpinner
						color="primary"
						variant="contained"
						className={classes.acceptButton}
						size="small"
						loading={loading}
						onClick={e => handleAcepptTicket(ticket.id)}
					>
						{i18n.t("ticketsList.buttons.accept")}
					</ButtonWithSpinner>
				)}
			</ListItem>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
